// Le tableau de marge d'un mois : ce que les chantiers ont rapporté, ce que les
// achats ont coûté, ce qu'il reste.
//
// Le calcul vit dans @imp/shared/marge-mensuelle, pur et testé à part ; ce
// fichier ne fait que le mettre en page. Le mois choisi appartient à la page
// Pilotage, parce que les tableaux d'équipe qui suivent doivent parler du même.

import { BOM_UTF8, csvDepuisLignes } from '@imp/shared/csv';
import { type LigneMarge, type MoisMarge } from '@imp/shared/marge-mensuelle';
import { FileDown } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import { formatEuro } from '@/modules/catalogue/api';

function eur(n: number): string {
  return formatEuro(n);
}

/** Rouge quand on perd de l'argent, vert quand on en gagne. */
function margeClass(m: number): string {
  return m < 0 ? 'text-prio-high' : 'text-money-in';
}

function pourcent(t: number | null): string {
  return t === null ? '\u2014' : `${String(Math.round(t * 100))}\u00a0%`;
}

export interface MargeMoisCardProps {
  mois: MoisMarge[];
  moisCourant: MoisMarge | undefined;
  sansMois: LigneMarge[];
  chargement: boolean;
  nomChef: (l: LigneMarge) => string;
  onChangerMois: (cle: string) => void;
}

export function MargeMoisCard({
  mois,
  moisCourant,
  sansMois,
  chargement,
  nomChef,
  onChangerMois,
}: MargeMoisCardProps) {
  // Le tableau du mois, tel quel, pour le tableur du comptable.
  const exporterCsv = () => {
    if (moisCourant === undefined) return;
    const entetes = ['Chantier', 'Libellé', 'CA HT', 'Achats', 'Marge', 'Taux %', 'Source'];
    const lignesCsv = moisCourant.lignes.map((l) => [
      l.numero,
      l.libelle,
      l.caHt.toFixed(2),
      l.achats.toFixed(2),
      l.marge.toFixed(2),
      l.tauxMarge === null ? '' : (l.tauxMarge * 100).toFixed(1),
      l.source === 'facture' ? 'Facturé' : 'Prévu (devis)',
    ]);
    const csv = csvDepuisLignes([entetes, ...lignesCsv]);
    const url = URL.createObjectURL(new Blob([BOM_UTF8 + csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `marge-${moisCourant.cle}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const lignes = moisCourant?.lignes ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <CardTitle>Marge du mois</CardTitle>
          <p className="text-muted-foreground mt-1 text-sm">
            Chiffre d&apos;affaires HT des chantiers du mois, moins les achats fournisseurs.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            className="h-10 w-auto"
            aria-label="Mois"
            value={moisCourant?.cle ?? ''}
            onChange={(e) => {
              onChangerMois(e.target.value);
            }}
          >
            {mois.map((m) => (
              <option key={m.cle} value={m.cle}>
                {m.label}
              </option>
            ))}
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={exporterCsv}
            disabled={moisCourant === undefined}
          >
            <FileDown className="h-4 w-4" /> CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {chargement ? (
          <p className="text-muted-foreground text-sm">Calcul…</p>
        ) : moisCourant === undefined ? (
          <p className="text-muted-foreground py-4 text-sm">
            Aucun chantier à afficher : ni facture émise, ni montant saisi sur les fiches.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {[
                { label: 'CA HT du mois', valeur: eur(moisCourant.caHt), teinte: '' },
                {
                  label: 'Achats',
                  valeur: eur(moisCourant.achats),
                  teinte: 'text-muted-foreground',
                },
                {
                  label: 'Marge',
                  valeur: eur(moisCourant.marge),
                  teinte: margeClass(moisCourant.marge),
                },
                {
                  label: 'Taux de marge',
                  valeur: pourcent(moisCourant.tauxMarge),
                  teinte: margeClass(moisCourant.marge),
                },
              ].map((k) => (
                <div key={k.label} className="rounded-lg border p-3">
                  <div className="text-muted-foreground text-[13px] font-semibold uppercase tracking-[0.04em]">
                    {k.label}
                  </div>
                  <div className={`mt-1 text-2xl font-bold tabular-nums ${k.teinte}`}>
                    {k.valeur}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-muted-foreground border-y text-left text-xs uppercase">
                  <tr>
                    <th className="px-4 py-2 font-medium">Chantier</th>
                    <th className="px-4 py-2 text-right font-medium">CA HT</th>
                    {/* Sur un téléphone, l'écran garde ce qu'on vient chercher : le CA,
                la marge, le taux. Les achats et le chef reviennent dès qu'il y a
                de la place — le détail complet reste dans le CSV. */}
                    <th className="hidden px-4 py-2 text-right font-medium sm:table-cell">
                      Achats
                    </th>
                    <th className="px-4 py-2 text-right font-medium">Marge</th>
                    <th className="px-4 py-2 text-right font-medium">Taux</th>
                    <th className="hidden px-4 py-2 font-medium md:table-cell">Chef</th>
                  </tr>
                </thead>
                <tbody>
                  {lignes.map((l) => (
                    <tr key={l.chantierId} className="hover:bg-muted/40 border-b last:border-0">
                      <td className="px-4 py-2">
                        <Link
                          to={`/chantiers/${l.chantierId}`}
                          className="text-primary hover:underline"
                        >
                          {l.numero}
                        </Link>{' '}
                        <span className="text-muted-foreground">{l.libelle}</span>
                        {/* Un chantier pas encore facturé annonce un montant
                          attendu, pas un montant acquis : l'écran le dit. */}
                        {l.source === 'devis' ? (
                          <span className="bg-muted text-muted-foreground ml-2 rounded-sm px-1.5 py-0.5 text-xs">
                            prévu
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">{eur(l.caHt)}</td>
                      <td className="text-muted-foreground hidden px-4 py-2 text-right tabular-nums sm:table-cell">
                        {eur(l.achats)}
                      </td>
                      <td
                        className={`px-4 py-2 text-right font-medium tabular-nums ${margeClass(l.marge)}`}
                      >
                        {eur(l.marge)}
                      </td>
                      <td className={`px-4 py-2 text-right tabular-nums ${margeClass(l.marge)}`}>
                        {pourcent(l.tauxMarge)}
                      </td>
                      <td className="text-muted-foreground hidden px-4 py-2 md:table-cell">
                        {nomChef(l)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-muted/30 border-t-2 font-semibold">
                    <td className="px-4 py-2">Total {moisCourant.label}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{eur(moisCourant.caHt)}</td>
                    <td className="hidden px-4 py-2 text-right tabular-nums sm:table-cell">
                      {eur(moisCourant.achats)}
                    </td>
                    <td
                      className={`px-4 py-2 text-right tabular-nums ${margeClass(moisCourant.marge)}`}
                    >
                      {eur(moisCourant.marge)}
                    </td>
                    <td
                      className={`px-4 py-2 text-right tabular-nums ${margeClass(moisCourant.marge)}`}
                    >
                      {pourcent(moisCourant.tauxMarge)}
                    </td>
                    <td className="hidden md:table-cell" />
                  </tr>
                </tfoot>
              </table>
            </div>

            <p className="text-muted-foreground mt-3 text-xs">
              Un chantier compte dans le mois de sa dernière facture émise. Tant qu&apos;il
              n&apos;est pas facturé, il est porté au mois de sa fin de travaux prévue, sur le
              montant saisi sur sa fiche — ces lignes-là portent la mention « prévu ». Les achats
              étant saisis en un seul montant par chantier, ils comptent en entier dans ce mois.
            </p>

            {sansMois.length > 0 ? (
              <p className="text-prio-mid mt-2 text-xs">
                {sansMois.length} chantier{sansMois.length > 1 ? 's' : ''} sans aucune date n&apos;
                {sansMois.length > 1 ? 'apparaissent' : 'apparaît'} dans aucun mois (
                {eur(sansMois.reduce((s, l) => s + l.marge, 0))} de marge) : renseigner leurs dates
                de travaux ou émettre leur facture les fera entrer dans le tableau.
              </p>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}
